// Phase K.3 (2026-08-06) — Customer statement detail page.
//
// Route: /customer/statements/:id (customer role only)
//
// Design:
//   * Header: statement id, period, status badge, total (large)
//   * Invoices table: per-invoice number, service period, amount
//   * Payment allocation section (only when paid)
//   * Actions: Download PDF, Pay now (only when sent)
//   * `?pay_return=1` query param means Stripe just redirected the
//     customer back after the FC/Elements flow — show a "processing"
//     card and poll the detail endpoint for status changes.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import StatementPayModal from "@/components/StatementPayModal";
import CreditMemosCard from "@/components/CreditMemosCard";
import {
    formatStatementIdentifier, formatStatementPeriod,
} from "@/lib/statementDisplay";
import {
    ArrowLeft, Download, CreditCard, AlertTriangle, CheckCircle2,
    Clock, Loader2, FileText,
} from "lucide-react";

const STATUS_LABEL = {
    sent: "Sent", processing: "Processing",
    paid: "Paid", failed: "Payment failed",
};
const STATUS_COLOR = {
    sent: "bg-blue-50 text-blue-900 ring-blue-200",
    processing: "bg-amber-50 text-amber-900 ring-amber-200",
    paid: "bg-emerald-100 text-emerald-900 ring-emerald-200",
    failed: "bg-red-50 text-red-700 ring-red-200",
};

const POLL_INTERVAL_MS = 5000;
const POLL_MAX = 12;

function fmtMoney(cents) {
    if (cents == null) return "—";
    const sign = cents < 0 ? "-" : "";
    const v = Math.abs(cents) / 100;
    return `${sign}$${v.toLocaleString(undefined, {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
    })}`;
}
function fmtDate(s) { if (!s) return "—"; return s.slice(0, 10); }
function fmtDateTime(s) {
    if (!s) return "—";
    try { return new Date(s).toLocaleString(); } catch { return s; }
}

export default function CustomerStatementDetailPage() {
    const { id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [statement, setStatement] = useState(null);
    const [loading, setLoading] = useState(true);
    const [payOpen, setPayOpen] = useState(false);
    // Optimistic "we just returned from Stripe" state.
    const [postReturn, setPostReturn] = useState(false);
    const pollCountRef = useRef(0);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/customer/statements/${id}`);
            setStatement(data);
            return data;
        } catch (err) {
            toast.error(formatApiError(err));
            return null;
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    // Handle Stripe redirect return.
    useEffect(() => {
        if (searchParams.get("pay_return") === "1") {
            setPostReturn(true);
            // Strip the query param but keep the path.
            const next = new URLSearchParams(searchParams);
            next.delete("pay_return");
            setSearchParams(next, { replace: true });
        }
    }, []);  // eslint-disable-line

    // Poll for status flip after Stripe return.
    useEffect(() => {
        if (!postReturn) return;
        if (!statement) return;
        if (["paid", "failed"].includes(statement.status)) {
            setPostReturn(false);
            pollCountRef.current = 0;
            return;
        }
        if (pollCountRef.current >= POLL_MAX) {
            setPostReturn(false);
            return;
        }
        const t = setTimeout(() => {
            pollCountRef.current += 1;
            load();
        }, POLL_INTERVAL_MS);
        return () => clearTimeout(t);
    }, [postReturn, statement, load]);

    const handleDownloadPdf = async () => {
        try {
            const res = await api.get(
                `/customer/statements/${id}/pdf?download=true`,
                { responseType: "blob" },
            );
            const blob = new Blob([res.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `statement-${id.slice(0, 8)}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => window.URL.revokeObjectURL(url), 1000);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    if (loading) {
        return (
            <DashboardShell>
                <div className="py-16 text-center text-slate-500"
                     data-testid="customer-statement-detail-loading">
                    <Loader2 className="h-6 w-6 animate-spin inline mr-2" />
                    Loading…
                </div>
            </DashboardShell>
        );
    }
    if (!statement) {
        return (
            <DashboardShell>
                <div className="py-16 text-center text-slate-500">
                    Statement not found.
                </div>
            </DashboardShell>
        );
    }

    const netDue = Math.max(
        0, Number(statement.total_cents || 0) - Number(statement.refunded_amount_cents || 0),
    );
    const canPay = statement.status === "sent" && netDue > 0;
    const lineSummary = statement.line_summary || [];

    return (
        <DashboardShell>
            <div className="mx-auto max-w-4xl px-4 py-8"
                 data-testid="customer-statement-detail-page">
                <div className="mb-4">
                    <Link
                        to="/customer/statements"
                        className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1"
                        data-testid="customer-statement-back"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        All statements
                    </Link>
                </div>

                <Card className="mb-6">
                    <CardContent className="p-6">
                        <div className="flex items-start justify-between gap-4 mb-4">
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <h1 className="text-xl font-semibold text-slate-900"
                                        data-testid="customer-statement-header">
                                        {formatStatementIdentifier(statement)}
                                    </h1>
                                    <span
                                        className={`text-xs px-2 py-0.5 rounded ring-1 ${
                                            STATUS_COLOR[statement.status] || "bg-slate-100"
                                        }`}
                                        data-testid="customer-statement-status"
                                    >
                                        {STATUS_LABEL[statement.status] || statement.status}
                                    </span>
                                </div>
                                {statement.customer_name && (
                                    <div className="text-sm text-slate-600">
                                        {statement.customer_name}
                                    </div>
                                )}
                                <div className="text-sm text-slate-500">
                                    Period {formatStatementPeriod(
                                        statement.period_start_date || statement.period_start,
                                        statement.period_end_date || statement.period_end,
                                    )}
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-xs uppercase tracking-wider text-slate-500">
                                    Total
                                </div>
                                <div className="text-3xl font-semibold text-slate-900 mt-0.5"
                                     data-testid="customer-statement-total">
                                    {fmtMoney(statement.total_cents)}
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-2">
                            {canPay && (
                                <Button
                                    onClick={() => setPayOpen(true)}
                                    className="gap-2 bg-blue-900 hover:bg-blue-800"
                                    data-testid="customer-statement-pay-btn"
                                >
                                    <CreditCard className="h-4 w-4" />
                                    Pay {fmtMoney(statement.total_cents)}
                                </Button>
                            )}
                            <Button
                                variant="outline"
                                onClick={handleDownloadPdf}
                                className="gap-2"
                                data-testid="customer-statement-pdf-btn"
                            >
                                <Download className="h-4 w-4" />
                                Download PDF
                            </Button>
                        </div>

                        {postReturn && statement.status === "processing" && (
                            <div className="mt-4 rounded-md bg-amber-50 ring-1 ring-amber-200 p-3 text-sm text-amber-900 flex items-start gap-2"
                                 data-testid="customer-statement-processing-banner">
                                <Clock className="h-4 w-4 mt-0.5" />
                                <div>
                                    <div className="font-medium">Payment authorized — settling now</div>
                                    <div className="text-xs mt-0.5">
                                        ACH debits typically settle in 3–5 business days. We&rsquo;ll email you when it clears.
                                    </div>
                                </div>
                            </div>
                        )}

                        {statement.status === "paid" && (
                            <div className="mt-4 rounded-md bg-emerald-50 ring-1 ring-emerald-200 p-3 text-sm text-emerald-900 flex items-start gap-2"
                                 data-testid="customer-statement-paid-banner">
                                <CheckCircle2 className="h-4 w-4 mt-0.5" />
                                <div>
                                    <div className="font-medium">Statement paid in full</div>
                                    <div className="text-xs mt-0.5">
                                        Settled at {fmtDateTime(statement.paid_at)}
                                    </div>
                                </div>
                            </div>
                        )}

                        {statement.status === "failed" && (
                            <div className="mt-4 rounded-md bg-red-50 ring-1 ring-red-200 p-3 text-sm text-red-800 flex items-start gap-2"
                                 data-testid="customer-statement-failed-banner">
                                <AlertTriangle className="h-4 w-4 mt-0.5" />
                                <div>
                                    <div className="font-medium">Charge failed</div>
                                    <div className="text-xs mt-0.5">
                                        {statement.failure_reason || "Your bank rejected the debit."}
                                        {" "}Please contact your account manager to retry.
                                    </div>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-6">
                        <h2 className="text-sm font-medium tracking-[0.18em] uppercase text-slate-500 mb-3 flex items-center gap-2">
                            <FileText className="h-4 w-4" />
                            Invoices in this statement ({lineSummary.length})
                        </h2>
                        {lineSummary.length === 0 && (
                            <div className="text-sm text-slate-500 py-4">
                                No invoices bundled on this statement.
                            </div>
                        )}
                        {lineSummary.length > 0 && (
                            <>
                                {/* Phase K.5.2 (2026-08-07) — mobile: stacked-card list. */}
                                <div className="sm:hidden space-y-2"
                                     data-testid="customer-statement-lines-mobile">
                                    {lineSummary.map((line) => (
                                        <div key={line.invoice_id}
                                             className="border rounded-md p-3 bg-slate-50"
                                             data-testid={`customer-statement-line-${line.invoice_id}`}>
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="font-medium text-slate-900 text-sm">
                                                    {line.invoice_number || "—"}
                                                    {line.prorated && (
                                                        <span className="ml-2 text-xs text-amber-700">
                                                            (prorated)
                                                        </span>
                                                    )}
                                                </span>
                                                <span className="text-sm font-semibold text-slate-900 tabular-nums">
                                                    {fmtMoney(line.amount_cents)}
                                                </span>
                                            </div>
                                            <div className="text-xs text-slate-500 break-words">
                                                {line.service_period_start || "—"} → {line.service_period_end || "—"}
                                            </div>
                                        </div>
                                    ))}
                                    <div className="flex items-center justify-between p-3 border-t pt-3 mt-2">
                                        <span className="text-sm font-semibold text-slate-500">Total</span>
                                        <span className="text-lg font-bold text-slate-900 tabular-nums">
                                            {fmtMoney(statement.total_cents)}
                                        </span>
                                    </div>
                                </div>

                                {/* Desktop/tablet: full table (unchanged). */}
                                <table className="hidden sm:table w-full text-sm"
                                       data-testid="customer-statement-invoices-table">
                                    <thead>
                                        <tr className="text-xs uppercase text-slate-500 border-b">
                                            <th className="text-left py-2">Invoice</th>
                                            <th className="text-left py-2">Service period</th>
                                            <th className="text-right py-2">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {lineSummary.map((line) => (
                                            <tr key={line.invoice_id}
                                                className="border-b last:border-0"
                                                data-testid={`customer-statement-line-${line.invoice_id}`}>
                                                <td className="py-2 font-medium text-slate-900">
                                                    {line.invoice_number || "—"}
                                                    {line.prorated && (
                                                        <span className="ml-2 text-xs text-amber-700">
                                                            (prorated)
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="py-2 text-xs text-slate-500">
                                                    {line.service_period_start || "—"} → {line.service_period_end || "—"}
                                                </td>
                                                <td className="py-2 text-right font-medium">
                                                    {fmtMoney(line.amount_cents)}
                                                </td>
                                            </tr>
                                        ))}
                                        <tr>
                                            <td colSpan={2} className="py-3 text-right font-semibold">
                                                Total
                                            </td>
                                            <td className="py-3 text-right font-semibold text-slate-900">
                                                {fmtMoney(statement.total_cents)}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </>
                        )}
                    </CardContent>
                </Card>

                {/* Phase K.4 (2026-08-07) — Credit memos card. */}
                <CreditMemosCard
                    memos={statement.credit_memos}
                    paidCents={statement.total_cents}
                    refundedCents={statement.refunded_amount_cents}
                />

                <StatementPayModal
                    statement={statement}
                    open={payOpen}
                    onOpenChange={setPayOpen}
                    onInitiated={() => setPostReturn(true)}
                />
            </div>
        </DashboardShell>
    );
}
